/** Error codes surfaced by render-document parsing. Every failure is explicit; nothing is silently skipped. */
export type RendererErrorCode =
  | 'SCHEMA_VERSION_UNSUPPORTED'
  | 'MISSING_FIELD'
  | 'INVALID_TYPE'
  | 'INVALID_VALUE'
  | 'OUT_OF_BOUNDS'
  | 'DUPLICATE_WIDGET_ID'
  | 'DUPLICATE_ID'
  | 'LIST_TOO_LONG'
  | 'UNKNOWN_WIDGET_TYPE'
  | 'TOO_MANY_WIDGETS'
  | 'UNKNOWN_FIELD';

/** One deterministic validation failure against the frozen kiosks wire contract. */
export interface RendererIssue {
  /** JSON-pointer-like path, e.g. `layout[3].position.x`. `$` is the document root. */
  readonly path: string;
  readonly code: RendererErrorCode;
  readonly message: string;
}

/** Result of parsing an unknown value into a `KioskRenderDocument`. */
export type ParseOutcome<T> = { ok: true; value: T } | { ok: false; issues: readonly RendererIssue[] };

export function failParse<T>(issues: readonly RendererIssue[]): ParseOutcome<T> {
  return { ok: false, issues };
}

export function okParse<T>(value: T): ParseOutcome<T> {
  return { ok: true, value };
}

/** Formats issues into a stable, diagnosable, non-secret message. */
export function formatIssues(issues: readonly RendererIssue[]): string {
  if (issues.length === 0) return 'Invalid kiosk render document';
  return `Invalid kiosk render document: ${issues
    .map((issue) => `${issue.path} [${issue.code}] ${issue.message}`)
    .join('; ')}`;
}

/**
 * Thrown by the assert-style entry points. Carries the full issue list so host
 * UIs can render a recoverable "template unavailable" state without leaking
 * internals: messages only ever contain paths, codes, and wire field names.
 */
export class KioskRendererError extends Error {
  readonly issues: readonly RendererIssue[];

  constructor(issues: readonly RendererIssue[]) {
    super(formatIssues(issues));
    this.name = 'KioskRendererError';
    this.issues = Object.freeze([...issues]);
  }
}
